import { FC, ReactNode, useState } from 'react';
import { Avatar, Col, Flex, Row, Typography } from 'antd';
import { TbChevronDown, TbChevronRight } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

interface CollapsibleSectionProps {
    children: ReactNode;
    description: string;
    icon: ReactNode;
    title: string;
    defaultOpen?: boolean;
}

export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
    children,
    defaultOpen = true,
    description,
    icon,
    title,
}) => {
    const { token } = useAntDesignTheme();
    const [open, setOpen] = useState(defaultOpen);

    return (
        <section style={{ borderBottom: `1px solid ${token.colorBorder}` }}>
            <button
                type='button'
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
                style={{
                    alignItems: 'flex-start',
                    background: 'transparent',
                    border: 0,
                    color: token.colorText,
                    cursor: 'pointer',
                    display: 'flex',
                    fontFamily: token.fontFamily,
                    gap: '0.5rem',
                    padding: '1rem',
                    textAlign: 'left',
                    width: '100%',
                }}
            >
                {open ? (
                    <TbChevronDown style={{ color: token.colorTextTertiary, marginTop: '0.15rem' }} />
                ) : (
                    <TbChevronRight style={{ color: token.colorTextTertiary, marginTop: '0.15rem' }} />
                )}
                <span style={{ color: token.colorTextTertiary, marginTop: '0.1rem' }}>{icon}</span>
                <span style={{ minWidth: 0 }}>
                    <strong style={{ color: token.colorText, fontFamily: token.fontFamily }}>{title}</strong>
                    <small
                        style={{
                            color: token.colorTextTertiary,
                            display: 'block',
                            fontFamily: token.fontFamily,
                            marginTop: '0.25rem',
                        }}
                    >
                        {description}
                    </small>
                </span>
            </button>
            {open ? <div style={{ padding: '0 1rem 1rem 3.25rem' }}>{children}</div> : null}
        </section>
    );
};

interface SettingRowProps {
    control: ReactNode;
    description: string;
    icon: ReactNode;
    label: string;
    last?: boolean;
}

export const SettingRow: FC<SettingRowProps> = ({ control, description, icon, label, last = false }) => {
    const { token } = useAntDesignTheme();

    return (
        <Row
            align='middle'
            gutter={[12, 8]}
            wrap={false}
            style={{ borderBottom: last ? undefined : `1px solid ${token.colorBorder}`, padding: '0.75rem 0' }}
        >
            <Col flex='none'>
                <Avatar
                    shape='square'
                    size={28}
                    icon={icon}
                    style={{ color: token.colorTextTertiary, border: `1px solid ${token.colorBorder}` }}
                />
            </Col>
            <Col flex='auto' style={{ minWidth: 0 }}>
                <Typography.Text strong>{label}</Typography.Text>
                <small style={{ color: token.colorTextTertiary, display: 'block' }}>{description}</small>
            </Col>
            <Col flex='none'>{control}</Col>
        </Row>
    );
};

interface VantagePropertyRowProps {
    children: ReactNode;
    description: string;
    icon: ReactNode;
    label: string;
    last?: boolean;
}

export const VantagePropertyRow: FC<VantagePropertyRowProps> = ({
    children,
    description,
    icon,
    label,
    last = false,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <Flex
            align='center'
            justify='space-between'
            style={{
                borderBottom: last ? undefined : `1px solid ${token.colorBorderSecondary}`,
                gap: '1rem',
                minHeight: '2.75rem',
                padding: '0.5rem 0',
                width: '100%',
            }}
            wrap='nowrap'
        >
            <Flex align='center' gap='0.75rem' style={{ flex: 1, minWidth: 0 }}>
                <Avatar
                    shape='square'
                    size={28}
                    icon={icon}
                    style={{
                        alignItems: 'center',
                        border: `1px solid ${token.colorBorder}`,
                        color: token.colorTextTertiary,
                        display: 'inline-flex',
                        flex: 'none',
                        justifyContent: 'center',
                    }}
                />
                <span style={{ minWidth: 0 }}>
                    <Typography.Text style={{ color: token.colorText }}>{label}</Typography.Text>
                    <small style={{ color: token.colorTextTertiary, display: 'block' }}>{description}</small>
                </span>
            </Flex>
            <Flex align='center' justify='flex-end' style={{ flex: 1, minWidth: 0 }}>
                {children}
            </Flex>
        </Flex>
    );
};
